import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Users, UserCheck } from 'lucide-react';
import { liveChatApi } from '../../../api/liveChat.api';
import { usersApi } from '../../../api';
import { resolveUserAvatar } from '../../../utils/media';

const AgentDetailsPage = () => {
  const [agents, setAgents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [activeAssignments, setActiveAssignments] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersResponse, sessionsResponse, assignmentsResponse] = await Promise.all([
        usersApi.getUsers(),
        liveChatApi.getSessions(),
        liveChatApi.getActiveAssignments(),
      ]);
      const usersPayload = usersResponse?.data?.data ?? usersResponse?.data ?? [];
      const agentList = Array.isArray(usersPayload)
        ? usersPayload.filter((item) => String(item.u_role || item.role || '').toLowerCase() === 'agent')
        : [];
      const sessionsPayload = sessionsResponse?.data?.data ?? sessionsResponse?.data ?? {};
      const sessionList = Array.isArray(sessionsPayload.sessions) ? sessionsPayload.sessions : [];
      const assignmentsPayload = assignmentsResponse?.data?.data ?? assignmentsResponse?.data ?? [];
      const assignmentList = Array.isArray(assignmentsPayload) ? assignmentsPayload : [];
      setAgents(agentList);
      setSessions(sessionList);
      setActiveAssignments(assignmentList);
    } catch (error) {
      setAgents([]);
      setSessions([]);
      setActiveAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleStatusChange = async (agent) => {
    const newVal = agent.u_active === 1 ? 0 : 1;
    try {
      await usersApi.updateAgentStatus(agent.uid, { active: newVal });
      setAgents((prev) => prev.map((item) => (item.uid === agent.uid ? { ...item, u_active: newVal } : item)));
    } catch (error) {
      // No-op: keep UI stable
    }
  };

  const openAssignDialog = (agent) => {
    setSelectedAgent(agent);
    setSelectedSessionId('');
    setAssignOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedAgent || !selectedSessionId) return;
    setAssigning(true);
    try {
      await liveChatApi.assignAgent({
        lcs_id: selectedSessionId,
        agent_id: selectedAgent.uid,
      });
      setAssignOpen(false);
      await loadData();
    } catch (error) {
      // No-op
    } finally {
      setAssigning(false);
    }
  };

  const activeAgentIds = new Set(
    activeAssignments
      .map((assignment) => assignment?.uid)
      .filter(Boolean)
      .map((id) => String(id))
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Users size={28} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Agent Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Allocate chats, monitor status, and manage availability.
          </Typography>
        </Box>
      </Stack>

      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Availability</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Loading agents...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : agents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        No agents found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  agents.map((agent) => {
                    const isAvailable = agent.u_active === 1 && !activeAgentIds.has(String(agent.uid));
                    return (
                    <TableRow key={agent.uid}>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar src={resolveUserAvatar(agent.u_image || '')}>
                            {(agent.u_name || 'A').charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600}>{agent.u_name || 'Agent'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {agent.uid}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{agent.u_email || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={agent.u_active === 1 ? 'Online' : 'Offline'}
                          color={agent.u_active === 1 ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Switch
                            size="small"
                            checked={agent.u_active === 1}
                            onChange={() => handleStatusChange(agent)}
                            color="success"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {agent.u_active === 1 ? (isAvailable ? 'Available' : 'Busy') : 'Inactive'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<UserCheck size={16} />}
                          onClick={() => openAssignDialog(agent)}
                          disabled={!isAvailable}
                        >
                          Assign Chat
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assign Chat</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Assign a waiting session to {selectedAgent?.u_name || 'agent'}.
            </Typography>
            {selectedAgent && activeAgentIds.has(String(selectedAgent.uid)) && (
              <Typography variant="caption" color="error.main">
                This agent is already assigned to an active chat.
              </Typography>
            )}
            <TextField
              select
              label="Select Session"
              value={selectedSessionId}
              onChange={(event) => setSelectedSessionId(event.target.value)}
            >
              {sessions
                .filter((sessionItem) => String(sessionItem.status || '').toLowerCase() === 'open')
                .map((sessionItem) => (
                <MenuItem key={sessionItem.lcs_id} value={sessionItem.lcs_id}>
                  Session #{sessionItem.lcs_id} - {sessionItem.cd_name || 'Customer'}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedSessionId || assigning}
            onClick={handleAssign}
          >
            {assigning ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentDetailsPage;
